/**
 * F-017: shared read-only line item component.
 *
 * Renders a single order line with product thumbnail, name, quantity x
 * unit price, and line total. Reused by F-017 (checkout summary), F-018
 * (order confirmation), and F-019 (order history).
 *
 * Props accept integer cents (passed directly to formatPrice).
 * `imagePath` and `imageUrl` are separate: caller resolves the storage
 * path via buildImageProxyUrl before passing; this component only
 * renders imageUrl.
 *
 * All strings go through useTranslation(). Logical Tailwind only.
 */
import { useTranslation } from '../i18n';
import { formatPrice } from '../lib/format';

export interface OrderLineItemProps {
  /** Product name (snapshot or live). */
  readonly name: string;
  /** Optional purchased/selected variant summary. Merchant-authored text. */
  readonly variantSummary?: string | null;
  /** Storage path (not URL). Caller resolves via buildImageProxyUrl. */
  readonly imagePath: string | null;
  /** Resolved image URL (caller passes the result of buildImageProxyUrl, or null). */
  readonly imageUrl: string | null;
  /** Number of units. */
  readonly quantity: number;
  /** Unit price in integer cents (agorot). Passed directly to formatPrice(). */
  readonly unitPriceCents: number;
  /** Line total in integer cents. Passed directly to formatPrice(). */
  readonly lineTotalCents: number;
  readonly variant?: 'legacy' | 'storefrontNeutral';
  /** Optional consumer foreground tokens; omitting them preserves existing app styling. */
  readonly textClassNames?: {
    readonly primary: string;
    readonly secondary: string;
  };
}

export function OrderLineItem({
  name,
  variantSummary,
  imageUrl,
  quantity,
  unitPriceCents,
  lineTotalCents,
  variant = 'legacy',
  textClassNames,
}: OrderLineItemProps): React.JSX.Element {
  const { lang, t } = useTranslation();
  const isStorefrontNeutral = variant === 'storefrontNeutral';
  const primaryText =
    textClassNames?.primary ??
    (isStorefrontNeutral
      ? 'text-gray-950 dark:text-gray-100'
      : 'text-stone-900 dark:text-stone-100');
  const secondaryText =
    textClassNames?.secondary ??
    (isStorefrontNeutral
      ? 'text-gray-500 dark:text-gray-400'
      : 'text-stone-500 dark:text-stone-400');
  const normalizedVariantSummary = variantSummary?.trim() ?? '';

  return (
    <div className="flex items-center gap-3 py-2">
      {/* Product thumbnail (64x64) */}
      <div
        className={
          isStorefrontNeutral
            ? 'h-16 w-16 shrink-0 overflow-hidden rounded-lg border border-gray-200 bg-gray-50 dark:border-gray-800 dark:bg-gray-900'
            : 'h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-stone-100 dark:bg-stone-800'
        }
      >
        {imageUrl ? (
          <img src={imageUrl} alt={name} className="h-full w-full object-cover" />
        ) : (
          <div
            className={
              isStorefrontNeutral
                ? 'flex h-full w-full items-center justify-center text-gray-400 dark:text-gray-500'
                : 'flex h-full w-full items-center justify-center text-stone-400 dark:text-stone-500'
            }
            aria-hidden="true"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z"
              />
            </svg>
          </div>
        )}
      </div>

      {/* Product info */}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className={`truncate text-sm font-medium ${primaryText}`}>{name}</span>
        {normalizedVariantSummary !== '' ? (
          <bdi
            data-testid="order-line-item-variant-summary"
            className={`truncate text-xs ${secondaryText}`}
          >
            {t('storefront.cart.line.variantSummary', {
              summary: normalizedVariantSummary,
              interpolation: { escapeValue: false },
            })}
          </bdi>
        ) : null}
        <span className={`text-xs ${secondaryText}`}>
          {quantity} &times; {formatPrice(unitPriceCents, lang)}
        </span>
      </div>

      {/* Line total */}
      <span className={`shrink-0 text-sm font-semibold ${primaryText}`}>
        {formatPrice(lineTotalCents, lang)}
      </span>
    </div>
  );
}
